import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import useSeo from '../hooks/useSeo';
import { contact, services } from '../data/site';

const inputClass =
  'mt-2 w-full rounded-md border border-ink-600 bg-ink-900 px-4 py-3 text-sm text-white placeholder:text-slate-500 transition focus:border-signal-400 focus:outline-none focus:ring-1 focus:ring-signal-400';

const Contact = () => {
  useSeo('/contact');
  const [searchParams] = useSearchParams();
  const preselected = services.find((service) => service.id === searchParams.get('service'));
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    interest: preselected ? preselected.title : '',
    message: '',
  });
  const [sent, setSent] = useState(false);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // There is no backend, so the form drafts an email in the visitor's mail client.
  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = `Project inquiry${form.interest ? `: ${form.interest}` : ''} from ${form.name}`;
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      form.company && `Company: ${form.company}`,
      form.interest && `Interested in: ${form.interest}`,
      '',
      form.message,
    ]
      .filter((line) => line !== false && line !== '')
      .join('\n');
    window.location.href = `mailto:${contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSent(true);
  };

  const details = [
    { icon: 'mail', label: 'Email', value: contact.email, href: `mailto:${contact.email}` },
    { icon: 'phone', label: 'Phone', value: contact.phone, href: contact.phoneHref },
    { icon: 'pin', label: 'Based in', value: contact.location },
    { icon: 'compass', label: 'Serving', value: contact.region },
  ];

  return (
    <>
      <PageHeader eyebrow="Contact" title="Let's build something.">
        Tell us about your product, your timeline and where you are stuck. We're based in Moncton, New Brunswick,
        work with businesses across Atlantic Canada, and usually reply within one business day.
      </PageHeader>

      <section className="container-site grid gap-14 py-20 lg:grid-cols-[1fr_1.6fr]">
        <Reveal>
          <ul className="space-y-8">
            {details.map((item) => (
              <li key={item.label} className="flex gap-4">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-ink-600 bg-ink-900 text-signal-400">
                  <Icon name={item.icon} className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-mono text-xs uppercase tracking-wider text-slate-500">{item.label}</span>
                  {item.href ? (
                    <a href={item.href} className="mt-1 block text-white hover:text-signal-400">
                      {item.value}
                    </a>
                  ) : (
                    <span className="mt-1 block text-white">{item.value}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.1} className="card p-6 sm:p-10">
          <form onSubmit={handleSubmit} className="grid gap-6 sm:grid-cols-2">
            <label className="text-sm font-medium text-slate-300">
              Name
              <input required name="name" autoComplete="name" value={form.name} onChange={update} className={inputClass} />
            </label>
            <label className="text-sm font-medium text-slate-300">
              Email
              <input
                required
                type="email"
                name="email"
                autoComplete="email"
                value={form.email}
                onChange={update}
                className={inputClass}
              />
            </label>
            <label className="text-sm font-medium text-slate-300">
              Company <span className="text-slate-500">(optional)</span>
              <input
                name="company"
                autoComplete="organization"
                value={form.company}
                onChange={update}
                className={inputClass}
              />
            </label>
            <label className="text-sm font-medium text-slate-300">
              I'm interested in
              <select name="interest" value={form.interest} onChange={update} className={inputClass}>
                <option value="">Select a service</option>
                {services.map((service) => (
                  <option key={service.id} value={service.title}>
                    {service.title}
                  </option>
                ))}
                <option value="Something else">Something else</option>
              </select>
            </label>
            <label className="text-sm font-medium text-slate-300 sm:col-span-2">
              Project details
              <textarea
                required
                name="message"
                rows="6"
                value={form.message}
                onChange={update}
                placeholder="What are you building, and what do you need help with?"
                className={inputClass}
              />
            </label>
            <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
              <button type="submit" className="btn-primary">
                Send message <Icon name="arrow" className="h-4 w-4" />
              </button>
              <p className="text-xs text-slate-500" role="status">
                {sent
                  ? `Your email app should now be open. If it didn't open, email us at ${contact.email}.`
                  : 'This opens a pre-filled email in your mail app.'}
              </p>
            </div>
          </form>
        </Reveal>
      </section>
    </>
  );
};

export default Contact;
